import { useEffect, useState, useMemo } from 'react';
import useSWRInfinite from 'swr/infinite';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

// Utils
import { get } from '@/utils/client';

// API Endpoints
import { SCHEDULER_PREVIEW_POSTS } from '@/lib/routes/apiEndpoints';

const getKey = (pageIndex, previousPageData, activeSPID, limit, requestParams) => {
    // Reached the end
    if (previousPageData && !previousPageData.posts) return null;

    const apiUrl = injectEndpointUrlParams(SCHEDULER_PREVIEW_POSTS, {
        SPID: activeSPID
    });

    // First page, we don't have `previousPageData`
    if (pageIndex === 0) return [`${apiUrl}?per_page=${limit}`, JSON.stringify(requestParams)];

    // Add the cursor to the API endpoint
    return [
        `${apiUrl}?per_page=${limit}&cursor=${previousPageData.cursor}`,
        JSON.stringify(requestParams)
    ];
};

const useSchedulerPreviewPosts = ({ activeSPID, limit = 20, params = null, options = {} } = {}) => {
    const [requestParams, setRequestParams] = useState(params);
    const [activeSocialProfile, setActiveSocialProfile] = useState(activeSPID);
    const [toSavePosts, setToSavePosts] = useState([]);

    useEffect(() => {
        setActiveSocialProfile(activeSPID);
    }, [activeSPID]);

    const [reorderedSchedulerPreviewPosts, setReorderedSchedulerPreviewPosts] = useState(null);

    const {
        data: dataSchedulerPreviewPosts,
        size,
        setSize,
        mutate: mutateSchedulerPreviewPosts,
        error: schedulerPreviewPostsError
    } = useSWRInfinite(
        (pageIndex, previousPageData) =>
            getKey(pageIndex, previousPageData, activeSocialProfile, limit, requestParams),
        ([url]) =>
            get(url, requestParams).then((res) => {
                return {
                    ...res.data,
                    posts: res.data.posts.filter((post) => !post?.read_only)
                };
            }),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            ...options
        }
    );

    const schedulerPreviewPosts = useMemo(() => {
        let arraySchedulerPreviewPosts = [];
        if (dataSchedulerPreviewPosts) {
            dataSchedulerPreviewPosts.map((page) => {
                arraySchedulerPreviewPosts = [
                    ...arraySchedulerPreviewPosts,
                    ...(page?.posts ?? [])
                ];
            });
        }

        if (reorderedSchedulerPreviewPosts) {
            const posts = [];
            let reorderedPosts = [];

            reorderedSchedulerPreviewPosts.map((reorderedPost) => {
                const existingPost = arraySchedulerPreviewPosts.find(
                    (post) => post.id === reorderedPost.id
                );

                if (
                    (existingPost && existingPost?.scheduled_at !== reorderedPost.scheduled_at) ||
                    reorderedPost?.id?.toString().split('-')[0] === 'draggable'
                ) {
                    reorderedPosts.push(reorderedPost);
                }

                if (existingPost || reorderedPost?.id?.toString().split('-')[0] === 'draggable') {
                    posts.push(reorderedPost);
                }
            });

            setToSavePosts(reorderedPosts);

            arraySchedulerPreviewPosts.map((previewPost) => {
                if (posts.findIndex((post) => post.id === previewPost.id) === -1) {
                    posts.push(previewPost);
                }
            });

            return posts;
        } else {
            setToSavePosts([]);
            return arraySchedulerPreviewPosts;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataSchedulerPreviewPosts, reorderedSchedulerPreviewPosts]);

    const isLoadingInitialData = !dataSchedulerPreviewPosts && !schedulerPreviewPostsError;
    const isLoadingMore =
        size > 0 &&
        dataSchedulerPreviewPosts &&
        typeof dataSchedulerPreviewPosts[size - 1] === 'undefined';

    return {
        schedulerPreviewPosts,
        totalCount: dataSchedulerPreviewPosts
            ? dataSchedulerPreviewPosts[0].summary.total_count
            : null,
        loadMoreSchedulerPreviewPosts: () => setSize(size + 1), // Function to load more posts (load more button)
        mutateSchedulerPreviewPosts,
        schedulerPreviewPostsError,
        setRequestParams, // Setter to change parameters in the url
        isLoadingInitialData, // Check if it's the initial loading (not the load more)
        isLoadMore:
            dataSchedulerPreviewPosts &&
            typeof dataSchedulerPreviewPosts[dataSchedulerPreviewPosts.length - 1].cursor ===
                'string', // Check if there is a cursor to activate the load more button
        isLoadingMore, // Check if the load more is loading
        setReorderedSchedulerPreviewPosts, // Set Reordered posts
        toSavePosts // Post which have been edited/reordered
    };
};

export default useSchedulerPreviewPosts;
